import {Table, TableBody, TableCell, TableContainer, TableRow} from "@mui/material";
import {OrdersGroupByDayGuidDto} from '@mybake/ui/models/Order/IOrderGuid';
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {useEffect, useRef, useState} from "react";
import {OrdersByDayGuid} from './OrderByDayGuid';
import {getOrderDateKey, getTodayDateKey} from './orderDisplay';

interface Props {
    orders: OrdersGroupByDayGuidDto[];
}

export const OrderTable = ({orders}: Props) => {
    const {t} = useAppTranslation();
    const [openDate, setOpenDate] = useState<string | null>(() => sessionStorage.getItem('ordersOpenDate'));
    const [openOrderId, setOpenOrderId] = useState<string | null>(() => {
        const storedOrderId = sessionStorage.getItem('ordersOpenOrderId');
        return storedOrderId || null;
    });
    const initializedForOrders = useRef(false);
    const restoredScroll = useRef(false);

    useEffect(() => {
        const availableDates = new Set(orders.map(day => getOrderDateKey(day.date)));

        if (!initializedForOrders.current) {
            initializedForOrders.current = true;
            if (openDate && availableDates.has(openDate)) return;
            const today = getTodayDateKey();
            if (availableDates.has(today)) setOpenDate(today);
            return;
        }

        if (openDate && !availableDates.has(openDate)) {
            setOpenDate(null);
            setOpenOrderId(null);
        }
    }, [openDate, orders]);

    useEffect(() => {
        if (openDate) sessionStorage.setItem('ordersOpenDate', openDate);
        else sessionStorage.removeItem('ordersOpenDate');
    }, [openDate]);

    useEffect(() => {
        if (openOrderId !== null) sessionStorage.setItem('ordersOpenOrderId', String(openOrderId));
        else sessionStorage.removeItem('ordersOpenOrderId');
    }, [openOrderId]);

    useEffect(() => {
        if (openOrderId === null || !openDate) return;
        const orderStillVisible = orders.some(day =>
            getOrderDateKey(day.date) === openDate && day.orders.some(order => order.id === openOrderId));
        if (!orderStillVisible) setOpenOrderId(null);
    }, [openDate, openOrderId, orders]);

    useEffect(() => {
        if (restoredScroll.current || !openDate) return;
        const storedScroll = sessionStorage.getItem('ordersScrollY');
        if (!storedScroll) return;
        restoredScroll.current = true;
        requestAnimationFrame(() => requestAnimationFrame(() => {
            window.scrollTo({top: Number(storedScroll), behavior: 'auto'});
            sessionStorage.removeItem('ordersScrollY');
        }));
    }, [openDate]);

    const handleToggleDate = (date: string) => {
        const dateKey = getOrderDateKey(date);
        setOpenDate(currentDate => currentDate === dateKey ? null : dateKey);
        setOpenOrderId(null);
    };

    const handleToggleOrder = (orderId: string) => {
        setOpenOrderId(currentOrderId => currentOrderId === orderId ? null : orderId);
    };

    let displayedWeekIndex = 0;
    const daysWithWeekStyle = orders.map((day, index) => {
        const weekKey = `${getOrderDateKey(day.date).slice(0, 4)}-${day.week.number}`;
        const previousDay = index > 0 ? orders[index - 1] : undefined;
        const previousWeekKey = previousDay
            ? `${getOrderDateKey(previousDay.date).slice(0, 4)}-${previousDay.week.number}`
            : weekKey;
        const startsNewWeek = index > 0 && previousWeekKey !== weekKey;

        if (startsNewWeek) displayedWeekIndex += 1;

        return {
            day,
            startsNewWeek,
            separatesDay: index > 0 && !startsNewWeek,
            alternateWeek: displayedWeekIndex % 2 === 1,
        };
    });

    return (<TableContainer sx={{overflowX: 'hidden'}}>
        <Table aria-labelledby="Orders by day" sx={{tableLayout: 'fixed', width: '100%'}}>
            <TableBody>
                {orders?.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={6}>{t('common:no_records_found')}</TableCell>
                    </TableRow>
                )}
                {daysWithWeekStyle.map(({day, startsNewWeek, separatesDay, alternateWeek}) => (
                    <OrdersByDayGuid
                        key={day.date}
                        ordersByDay={day}
                        startsNewWeek={startsNewWeek}
                        separatesDay={separatesDay}
                        alternateWeek={alternateWeek}
                        isOpen={openDate === getOrderDateKey(day.date)}
                        openOrderId={openOrderId}
                        onToggleDate={handleToggleDate}
                        onToggleOrder={handleToggleOrder}
                    />
                ))}
            </TableBody>
        </Table>
    </TableContainer>)
}
